import type { DataLayerEventPayloads } from '@/lib/analytics/data-layer';
import { clearSessionJSONItem, getSessionJSONItem, setSessionJSONItem } from '@/lib/utils';

export const PENDING_LOGIN_STORAGE_KEY = 'analytics:pendingLogin';
export const PENDING_LOGIN_HOLD_FALLBACK_MS = 4000;

export type PendingLogin = {
    method: DataLayerEventPayloads['login']['method'];
    target: DataLayerEventPayloads['login']['target'];
    email_address: string;
};

let holdReleased = false;
let sawProfileLoading = false;

function isPendingLogin(value: unknown): value is PendingLogin {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value as PendingLogin;
    return (
        (candidate.method === 'email' || candidate.method === 'google' || candidate.method === 'apple') &&
        (candidate.target === 'shop' || candidate.target === 'club') &&
        typeof candidate.email_address === 'string'
    );
}

function resetHoldCycle(): void {
    holdReleased = false;
    sawProfileLoading = false;
}

export function queuePendingLogin(pendingLogin: PendingLogin): void {
    setSessionJSONItem(PENDING_LOGIN_STORAGE_KEY, pendingLogin);
    resetHoldCycle();
}

export function peekPendingLogin(): PendingLogin | undefined {
    const stored = getSessionJSONItem<unknown>(PENDING_LOGIN_STORAGE_KEY);
    return isPendingLogin(stored) ? stored : undefined;
}

export function consumePendingLogin(): PendingLogin | undefined {
    const pendingLogin = peekPendingLogin();
    clearSessionJSONItem(PENDING_LOGIN_STORAGE_KEY);
    resetHoldCycle();
    return pendingLogin;
}

export function releasePendingLoginHold(): void {
    holdReleased = true;
}

export function getPendingLoginHoldKey(): string | null {
    const pendingLogin = peekPendingLogin();
    if (!pendingLogin) {
        return null;
    }
    return `${pendingLogin.method}:${pendingLogin.target}:${pendingLogin.email_address}`;
}

export function shouldHoldAuthLifecycle(input: {
    userType: 'registered' | 'guest' | undefined;
    isProfileLoading: boolean;
    hasAnalyticsProfile: boolean;
}): boolean {
    if (!peekPendingLogin() || input.userType !== 'registered' || holdReleased) {
        return false;
    }

    if (input.hasAnalyticsProfile) {
        holdReleased = true;
        return false;
    }

    if (input.isProfileLoading) {
        sawProfileLoading = true;
        return true;
    }

    if (sawProfileLoading) {
        holdReleased = true;
        return false;
    }

    return true;
}

export function resetPendingLoginForTests(): void {
    clearSessionJSONItem(PENDING_LOGIN_STORAGE_KEY);
    resetHoldCycle();
}
